import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from './store/authStore'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import SettingsPage from './pages/SettingsPage'
import MainChatScreen from './components/chat/MainChatScreen'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, checking } = useAuthStore()
  if (checking) {
    return (
      <div className="flex-center" style={{ width: '100%', height: '100dvh', background: 'var(--bg-primary)', flexDirection: 'column', gap: 12 }}>
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}>
          <Loader2 size={32} color="var(--accent)" />
        </motion.div>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p>
      </div>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function WelcomePage() {
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('zumo-theme') as 'dark' | 'light') || 'dark'
  )
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('zumo-theme', theme)
  }, [theme])

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100dvh', background: 'var(--bg-primary)', gap: 24, padding: 24 }}>
      <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}>
        <img src="/icon.jpeg" alt="Zumo" style={{ width: 88, height: 88, borderRadius: 22, boxShadow: '0 8px 32px rgba(0,209,88,0.2)' }} />
      </motion.div>
      <h1 style={{ fontSize: 38, fontWeight: 800, letterSpacing: -1, background: 'linear-gradient(135deg, #00d158, #00ff6e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Zumo</h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: 15, textAlign: 'center', lineHeight: 1.5 }}>Chat, call, connect — in real time.</p>
      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <a href="/login" style={{ padding: '14px 32px', background: 'var(--accent)', color: '#000', borderRadius: 14, textDecoration: 'none', fontSize: 15, fontWeight: 600 }}>Log In</a>
        <a href="/signup" style={{ padding: '14px 32px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-primary)', borderRadius: 14, textDecoration: 'none', fontSize: 15, fontWeight: 600 }}>Sign Up</a>
      </div>
      <button onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} style={{ padding: '10px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, marginTop: 8 }}>
        {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
      </button>
    </div>
  )
}

export default function App() {
  const { checkSession } = useAuthStore()
  useEffect(() => { checkSession() }, [])

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<WelcomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><MainChatScreen /></ProtectedRoute>} />
      </Routes>
    </BrowserRouter>
  )
}
